import React from 'react';
import { Field } from 'formik';
import AddressAutocomplete from '../../common/AddressAutocomplete';
import PhoneInput from '../../common/PhoneInput';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

const InformationsPersonnelles = ({ formikProps, user }) => {
  const { errors, touched, setFieldValue, values } = formikProps;

  // Vérifier si chaque champ d'identité est verrouillé individuellement
  // Un champ est verrouillé seulement si l'utilisateur est connecté ET que le champ a une valeur
  const isTypePersonneLocked = !!user && !!user.typePersonne;
  const isDesignationLocked = !!user && !!user.designation;
  const isNomLocked = !!user && !!user.nom;
  const isPrenomLocked = !!user && !!user.prenom;
  const isRaisonSocialeLocked = !!user && !!user.raisonSociale;
  
  // Afficher l'encart d'information si au moins un champ est verrouillé
  const hasLockedFields = isTypePersonneLocked || isDesignationLocked || isNomLocked || isPrenomLocked || isRaisonSocialeLocked;

  // Convertir la date en format YYYY-MM-DD pour l'input type="date"
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  // Options pour le type de personne
  const typePersonneOptions = [
    { value: 'personne_physique', label: 'Personne physique' },
    { value: 'association', label: 'Association' },
    { value: 'scea', label: 'SCEA' },
    { value: 'earl', label: 'EARL' },
    { value: 'etablissement_public', label: 'Établissement public' },
  ];

  // Vérifier si c'est une personne morale
  const isPersonneMorale = ['association', 'scea', 'earl', 'etablissement_public'].includes(
    values.informationsPersonnelles?.typePersonne
  );

  // Labels dynamiques
  const civiliteLabel = isPersonneMorale ? 'Civilité du représentant légal' : 'Civilité';
  const nomLabel = isPersonneMorale ? 'Nom du représentant légal' : 'Nom';
  const prenomLabel = isPersonneMorale ? 'Prénom du représentant légal' : 'Prénom(s)';

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Informations personnelles
        </h3>
      </div>

      {/* Encart d'information pour les champs non modifiables */}
      {hasLockedFields && (
        <div className="rounded-lg bg-honey-50 border border-honey-200 p-4">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-honey-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-honey-700">
                Les informations d'identité (type, civilité, nom, prénom, raison sociale) ne sont pas modifiables directement. 
                Pour toute modification, veuillez contacter l'administrateur depuis votre profil.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
        {/* Type de personne */}
        <div>
          <label htmlFor="typePersonne" className={`block text-sm font-medium ${isTypePersonneLocked ? 'text-slate-500' : 'text-gray-700'}`}>
            Type {!isTypePersonneLocked && <span className="text-red-500">*</span>}
          </label>
          <div className="mt-1">
            <Field
              as="select"
              name="informationsPersonnelles.typePersonne"
              id="typePersonne"
              disabled={isTypePersonneLocked}
              className={isTypePersonneLocked 
                ? "block w-full border border-slate-200 rounded-md shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                : "shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
              }
            >
              {typePersonneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Field>
          </div>
        </div>

        {/* Désignation */}
        <div>
          <label htmlFor="designation" className={`block text-sm font-medium ${isDesignationLocked ? 'text-slate-500' : 'text-gray-700'}`}>
            {civiliteLabel} {!isDesignationLocked && <span className="text-red-500">*</span>}
          </label>
          <div className="mt-1">
            <Field
              as="select"
              name="informationsPersonnelles.designation"
              id="designation"
              disabled={isDesignationLocked}
              className={isDesignationLocked 
                ? "block w-full border border-slate-200 rounded-md shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                : "shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
              }
            >
              <option value="">-- Sélectionner --</option>
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
            </Field>
          </div>
        </div>

        {/* Raison sociale - visible uniquement pour personne morale */}
        {isPersonneMorale && (
          <div className="sm:col-span-2">
            <label htmlFor="raisonSociale" className={`block text-sm font-medium ${isRaisonSocialeLocked ? 'text-slate-500' : 'text-gray-700'}`}>
              Raison sociale {!isRaisonSocialeLocked && <span className="text-red-500">*</span>}
            </label>
            <div className="mt-1">
              <Field
                type="text"
                name="informationsPersonnelles.raisonSociale"
                id="raisonSociale"
                disabled={isRaisonSocialeLocked}
                placeholder="Nom de l'association, SCEA, EARL ou établissement"
                className={isRaisonSocialeLocked 
                  ? "block w-full border border-slate-200 rounded-md shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                  : "shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
                }
              />
              {errors.informationsPersonnelles?.raisonSociale && touched.informationsPersonnelles?.raisonSociale && (
                <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.raisonSociale}</p>
              )}
            </div>
          </div>
        )}

        <div>
          <label htmlFor="nom" className={`block text-sm font-medium ${isNomLocked ? 'text-slate-500' : 'text-gray-700'}`}>
            {nomLabel} {!isNomLocked && <span className="text-red-500">*</span>}
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsPersonnelles.nom"
              id="nom"
              disabled={isNomLocked}
              className={isNomLocked 
                ? "block w-full border border-slate-200 rounded-md shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                : "shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
              }
            />
            {errors.informationsPersonnelles?.nom && touched.informationsPersonnelles?.nom && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.nom}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="prenom" className={`block text-sm font-medium ${isPrenomLocked ? 'text-slate-500' : 'text-gray-700'}`}>
            {prenomLabel} {!isPrenomLocked && <span className="text-red-500">*</span>}
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsPersonnelles.prenom"
              id="prenom"
              disabled={isPrenomLocked}
              className={isPrenomLocked 
                ? "block w-full border border-slate-200 rounded-md shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                : "shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
              }
            />
            {errors.informationsPersonnelles?.prenom && touched.informationsPersonnelles?.prenom && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.prenom}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Field
              type="email"
              name="informationsPersonnelles.email"
              id="email"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsPersonnelles?.email && touched.informationsPersonnelles?.email && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.email}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="dateNaissance" className="block text-sm font-medium text-gray-700">
            Date de naissance <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <input
              type="date"
              name="informationsPersonnelles.dateNaissance"
              id="dateNaissance"
              value={formatDateForInput(values.informationsPersonnelles.dateNaissance)}
              onChange={(e) => {
                const dateValue = e.target.value ? new Date(e.target.value) : null;
                setFieldValue('informationsPersonnelles.dateNaissance', dateValue);
              }}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsPersonnelles?.dateNaissance && touched.informationsPersonnelles?.dateNaissance && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.dateNaissance}</p>
            )}
          </div>
        </div>
        
        <div>
          <label htmlFor="telephoneMobile" className="block text-sm font-medium text-gray-700">
            Téléphone (portable) <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <PhoneInput
              name="informationsPersonnelles.telephoneMobile"
              id="telephoneMobile"
              value={values.informationsPersonnelles.telephoneMobile || ''}
              onChange={(value) => setFieldValue('informationsPersonnelles.telephoneMobile', value)}
              placeholder=""
              error={errors.informationsPersonnelles?.telephoneMobile && touched.informationsPersonnelles?.telephoneMobile}
            />
            {errors.informationsPersonnelles?.telephoneMobile && touched.informationsPersonnelles?.telephoneMobile && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.telephoneMobile}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="telephone" className="block text-sm font-medium text-gray-700">
            Téléphone (fixe)
          </label>
          <div className="mt-1">
            <PhoneInput
              name="informationsPersonnelles.telephone"
              id="telephone"
              value={values.informationsPersonnelles.telephone || ''}
              onChange={(value) => setFieldValue('informationsPersonnelles.telephone', value)}
              placeholder=""
              error={errors.informationsPersonnelles?.telephone && touched.informationsPersonnelles?.telephone}
            />
            {errors.informationsPersonnelles?.telephone && touched.informationsPersonnelles?.telephone && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.telephone}</p>
            )}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="rue" className="block text-sm font-medium text-gray-700">
            Adresse <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <AddressAutocomplete
              initialValue={values.informationsPersonnelles?.adresse?.rue || ''}
              placeholder="Commencez à taper votre adresse..."
              onInputChange={(value) => {
                setFieldValue('informationsPersonnelles.adresse.rue', value);
              }}
              onAddressSelect={(address) => {
                setFieldValue('informationsPersonnelles.adresse.rue', address.rue);
                setFieldValue('informationsPersonnelles.adresse.codePostal', address.codePostal);
                setFieldValue('informationsPersonnelles.adresse.ville', address.ville);
              }}
            />
            {errors.informationsPersonnelles?.adresse?.rue && touched.informationsPersonnelles?.adresse?.rue && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.adresse.rue}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="codePostal" className="block text-sm font-medium text-gray-700">
            Code postal <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsPersonnelles.adresse.codePostal"
              id="codePostal"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsPersonnelles?.adresse?.codePostal && touched.informationsPersonnelles?.adresse?.codePostal && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.adresse.codePostal}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="ville" className="block text-sm font-medium text-gray-700">
            Ville <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsPersonnelles.adresse.ville"
              id="ville"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsPersonnelles?.adresse?.ville && touched.informationsPersonnelles?.adresse?.ville && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsPersonnelles.adresse.ville}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InformationsPersonnelles;
